import styled from '@emotion/styled';
import { forwardRef, Ref, useEffect, useState } from 'react';
import { Season } from '../../apiService';
import EpisodeList from './EpisodeList';
import SeasonSelector from './SeasonSelector';
import useConditionalProps from '../../utils/useConditionalProps';
import { Flex, Text } from 'common/styles';
import { globalPadding } from 'common/Layout/Layout';
import { __t } from 'common/i18n';

type SeriesContentProps = {
  seasons: Season[];
  season?: number;
  episode?: number;
  onScrollToEpisodes?: () => void;
};

const Container = styled.div(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(7),
}));

const SeriesContent = ({ seasons, season: seasonNumber, onScrollToEpisodes }: SeriesContentProps, ref: Ref<HTMLDivElement>) => {
  const [index, setIndex] = useState<number>(findSelectedSeason(seasons, seasonNumber));
  const active: Season = seasons[index];
  const { episodes } = active || {};

  const reactions = useConditionalProps((s) => s.seasons);
  const activeReactions = reactions?.[active?.id || -1];

  const handleSeasonChange = (index: number) => {
    setIndex(index);
    onScrollToEpisodes?.();
  };

  useEffect(() => {
    setIndex(findSelectedSeason(seasons, seasonNumber));
  }, [seasonNumber, seasons]);

  return (
    <Container ref={ref}>
      <SeasonContainer mb={{ xs: 2, md: 1 }} align="center" justify={{ xs: 'space-between', md: 'flex-start' }}>
        {episodes?.length > 0 && <Text variant="h2" weight="medium">
          {__t('content.type.episodes')}
        </Text>}
        <SeasonSelector seasons={seasons} active={index} onChange={handleSeasonChange} />
      </SeasonContainer>
      {episodes?.length > 0 && <EpisodeList episodes={episodes} reactions={activeReactions} />}
    </Container>
  );
};

const SeasonContainer = styled(Flex)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    backgroundImage: `linear-gradient(to bottom, ${theme.palette.background[700][0]}, rgba(0,0,0,0))`,
    marginLeft: globalPadding.sm && `-${theme.spacings[globalPadding.sm]}rem`,
    marginRight: globalPadding.sm && `-${theme.spacings[globalPadding.sm]}rem`,
    padding: theme.spacing(1, 4),
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: globalPadding.xs && `-${theme.spacings[globalPadding.xs]}rem`,
    marginRight: globalPadding.xs && `-${theme.spacings[globalPadding.xs]}rem`,
    padding: theme.spacing(1, 2),
  },
}));

function findSelectedSeason(seasons: Season[], season?: number): number {
  if (!season) return 0;
  return seasons.findIndex((s) => s.season === season);
}

export default forwardRef<HTMLDivElement, SeriesContentProps>(SeriesContent);
